import { useMutation, useQuery } from '@apollo/client';
import classNames from 'classnames';
import React, { useState } from 'react';
import * as styles from './AdminApiToken.module.scss';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import Badge from '../../components/Badge';
import Button from '../../components/Button';
import Card from '../../components/Card';
import MetaList, { MetaListItem } from '../../components/MetaList';
import { gql } from '../../graphql/generated/gql';
import dockyBlank from '../../images/docky-blank.svg';
import { UIActions } from '../../ui/uiSlice';

const API_TOKEN_QUERY = gql(/** @complexity 1 */ `
  query apiToken($id: ID!) {
    apiToken(userId: $id)
  }
`);

const REGENERATE_API_TOKEN = gql(/** @complexity 2 */ `
  mutation regenerateApiToken($id: ID!) {
    regenerateApiToken(userId: $id)
  }
`);

type AdminApiTokenProps =
  | {
      id: string;
      name: string;
      fakeToken?: undefined;
    }
  | { id?: undefined; name: string; fakeToken: string };

const AdminApiToken = (props: AdminApiTokenProps) => {
  const { id, name } = props;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const [tokenRevealed, setTokenRevealed] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  const { data } = useQuery(API_TOKEN_QUERY, {
    variables: { id: id! },
    skip: !id || !tokenRevealed,
  });

  const [regenerateToken] = useMutation(REGENERATE_API_TOKEN);

  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const useFake = !data && !props.id;
  const showToken = tokenRevealed && data?.apiToken;

  const onRegenerateToken = () => {
    if (!id) {
      return;
    }
    regenerateToken({
      variables: { id },
      update: (proxy, result) => {
        const currentData = proxy.readQuery({
          query: API_TOKEN_QUERY,
          variables: { id },
        });

        if (currentData) {
          proxy.writeQuery({
            query: API_TOKEN_QUERY,
            variables: { id },
            data: {
              apiToken: result.data?.regenerateApiToken,
            },
          });
        }
      },
    });
  };

  const handleCopyButtonClick = async () => {
    if (data?.apiToken) {
      await navigator.clipboard.writeText(data.apiToken);
      dispatch(
        UIActions.pushToast({
          level: 'success',
          message: 'Token copied',
        }),
      );
    }
  };

  return (
    <Card noPadding>
      <div
        className={classNames(styles.wrapper, {
          [styles.fakeWrapper]: useFake,
        })}
      >
        <div className={styles.img}>
          <img src={dockyBlank} height="16" alt="Slab Logo" />
        </div>
        <div
          className={classNames(styles.content, {
            [styles.tokenRevealed]: showToken,
          })}
        >
          <div className={styles.nameWrapper}>
            <div className={styles.name}>{name}</div>
            <Badge className={styles.badge} color="pink" inline={false}>
              BOT
            </Badge>
          </div>
          {useFake ? (
            <div className={styles.subtitle}>{props.fakeToken}</div>
          ) : (
            <>
              {!showToken ? (
                <Button
                  variant="secondary"
                  onClick={() => setTokenRevealed(true)}
                >
                  Reveal token
                </Button>
              ) : (
                <div className={styles.tokenWithControls}>
                  {data?.apiToken}
                  <MetaList>
                    <MetaListItem>
                      <Button variant="link" onClick={handleCopyButtonClick}>
                        Copy
                      </Button>
                    </MetaListItem>
                    <MetaListItem>
                      <Button
                        variant="link"
                        onClick={() => onRegenerateToken()}
                      >
                        Regenerate
                      </Button>
                    </MetaListItem>
                  </MetaList>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

export default AdminApiToken;
